import {
  BackfillFeedType,
  BackfillPreviewResult,
  CompanyContentHealth,
} from '../../types/contentCompletion';
import { PrCoverageGapDetail } from '../../api/scheduledJobs';

const DAYS_BACK_MIN = 1;
const DAYS_BACK_MAX = 730;

const clampDaysBack = (value: number) =>
  Math.min(DAYS_BACK_MAX, Math.max(DAYS_BACK_MIN, value));

/**
 * How far back extractors look from *today* (not the gap size).
 *
 * Gap rows often show a small page−Neo4j delta (e.g. 8 days) while the page's
 * newest PR is weeks old. Using ``gap_days + 7`` then yields an empty preview.
 * Look back from today through Neo4j's last PR (or the page date) plus a buffer.
 */
export const daysBackForGap = (
  row: PrCoverageGapDetail,
  today: Date = new Date()
): number => {
  const todayUtc = Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate()
  );

  const daysSince = (isoDate: string): number | null => {
    const d = isoDate.slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
    const [y, m, day] = d.split('-').map(Number);
    const then = Date.UTC(y, m - 1, day);
    return Math.max(0, Math.ceil((todayUtc - then) / (1000 * 60 * 60 * 24)));
  };

  const neo = (row.neo4j_last_pr_date || '').trim();
  const page = (row.page_last_pr_date || '').trim();

  // Prefer Neo4j last: filterPreviewToGap keeps dates > neo, so the window must
  // reach from today back past neo (with buffer) to include those missing PRs.
  const sinceNeo = neo ? daysSince(neo) : null;
  if (sinceNeo != null) {
    return clampDaysBack(Math.max(sinceNeo + 7, 30));
  }

  const sincePage = page ? daysSince(page) : null;
  if (sincePage != null) {
    return clampDaysBack(Math.max(sincePage + 7, 30));
  }

  if (typeof row.gap_days === 'number' && row.gap_days > 0) {
    return clampDaysBack(Math.max(row.gap_days + 7, 30));
  }
  return 30;
};

export const feedTypeForGap = (row: PrCoverageGapDetail): BackfillFeedType => {
  const t = (row.feed_type || '').toLowerCase();
  if (t === 'rss') return 'rss';
  if (t === 'html') return 'html';
  return 'auto';
};

/** Build a minimal health row so existing backfill dialogs can resolve source URLs. */
export const companyFromGapDetail = (
  row: PrCoverageGapDetail
): CompanyContentHealth => {
  const source = (row.source_url || '').trim();
  const isRss = (row.feed_type || '').toLowerCase() === 'rss';
  return {
    company_id: row.company_id || '',
    company_name: row.company_name || row.company_id || '',
    corporate_status: row.corporate_status || undefined,
    press_release_url: isRss ? undefined : source || undefined,
    feed_news_rss: isRss ? source || undefined : undefined,
    has_feed_configured: Boolean(source),
    pr_total_count: 0,
    pr_recent_count: 0,
    quartr_total_count: 0,
    quartr_recent_count: 0,
    has_quartr_feed: false,
    status: 'stale',
    quartr_status: 'no_feed',
    last_pr_date: row.neo4j_last_pr_date || undefined,
  };
};

/** Keep only PRs strictly newer than Neo4j's last date (what the gap is about). */
export const filterPreviewToGap = (
  preview: BackfillPreviewResult,
  neo4jLast: string | null | undefined
): BackfillPreviewResult => {
  const cutoff = (neo4jLast || '').slice(0, 10);
  if (!cutoff) return preview;
  const items = (preview.items || []).filter((item) => {
    const d = (item.published_date || '').trim().slice(0, 10);
    return Boolean(d && d > cutoff);
  });
  return {
    ...preview,
    items,
    press_releases_found: items.length,
  };
};
